'use client';

import React from 'react';
import classNames from 'classnames';
import { useTranslate } from '@tolgee/react';
import { Caption, EmployeeBaseCard, ChevronRightIcon, Text, Subtitle } from '@/design-system';
import {
  CardCategoryLabel,
  CardCategory,
} from '@/design-system/components/Basic/Card/CardCategoryLabel';
import type { InstructionAssignmentResponseDto } from '@/types/Api';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';
import type { ContentLanguage } from '@/types/language';
import { formatDateToDe } from '@/utils/helpers';
import {
  getInstructionDisplayStatus,
  getInstructionStatusLabelKey,
} from '@/utils/instructionStatus';

export interface InstructionEmployeeCardProps {
  assignment: InstructionAssignmentResponseDto;
  to?: string;
  className?: string;
}

export const InstructionEmployeeCard: React.FC<InstructionEmployeeCardProps> = ({
  assignment,
  to,
  className,
}) => {
  const { t } = useTranslate();
  const language = useSelectedLanguage();
  const displayStatus = getInstructionDisplayStatus({
    status: assignment.status,
    dueDate: assignment.dueDate,
  });
  const statusLabelKey = getInstructionStatusLabelKey(displayStatus);
  const dueDateFormatted = formatDateToDe(assignment.dueDate);
  const completedAtFormatted = assignment.completedAt
    ? formatDateToDe(assignment.completedAt)
    : null;
  const contentLanguage: ContentLanguage = language?.toLowerCase().startsWith('en') ? 'en' : 'de';

  const badgeBg =
    displayStatus === 'OVERDUE'
      ? 'bg-ds-bg-feedback-soft-error'
      : displayStatus === 'SOON_DUE'
        ? 'bg-ds-bg-feedback-soft-info'
        : null;

  const localizedTitle =
    assignment.contentSummary?.localized?.title?.[contentLanguage] ??
    assignment.contentSummary?.title;
  return (
    <EmployeeBaseCard to={to} className={className}>
      <div className="flex flex-col gap-6 flex-1 min-h-0">
        {/* Header row */}
        <div className="flex justify-end items-center gap-2 shrink-0 pl-4 pr-2">
          {localizedTitle && (
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <Subtitle weight="semibold" className="line-clamp-3">
                {localizedTitle}
              </Subtitle>
            </div>
          )}
          {badgeBg && (
            <div
              className={classNames(
                'flex justify-center items-center gap-2.5 py-1 px-2 rounded-ds-2',
                badgeBg,
              )}
            >
              <Text size="s" weight="semibold" className="text-ds-text-1">
                {t(statusLabelKey)}
              </Text>
            </div>
          )}
          <ChevronRightIcon className="text-ds-navy-blue-900 w-4 h-4" aria-hidden />
        </div>

        {/* Content rows */}
        <div className="flex flex-col gap-6 min-w-0 px-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-0">
            <div className="flex flex-col gap-2 min-w-0">
              <Caption weight="medium" className="text-ds-text-3">
                {t('ohs-hub.instructions.card.dueDate')}
              </Caption>
              <Text size="s" weight="semibold" className="text-ds-text-1">
                {dueDateFormatted}
              </Text>
            </div>
          </div>
          {completedAtFormatted && (
            <div className="flex flex-col gap-2 min-w-fit">
              <Caption weight="medium" className="text-ds-text-3">
                {t('ohs-hub.instructions.card.completedAt')}
              </Caption>
              <Text size="s" weight="semibold" className="text-ds-text-1">
                {completedAtFormatted}
              </Text>
            </div>
          )}
        </div>
      </div>
      <CardCategoryLabel category={CardCategory.INSTRUCTION}>
        {t('ohs-hub.employeeDetails.instructionCard.label')}
      </CardCategoryLabel>
    </EmployeeBaseCard>
  );
};
